import httpClient from 'api/https-client';
import { useCallback, useEffect, useState } from 'react';
import { selectors, useAppSelector } from 'store';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

import { Bonus, BonusId } from './bonuses';

export type BonusUnlockStatus = 'unlocking' | 'unlocked';

type Statuses = Partial<Record<BonusId, BonusUnlockStatus>>;
type Errors = Partial<Record<BonusId, string>>;

const STORAGE_PREFIX = 'mvp-bonus-unlocks';
const NOT_ELIGIBLE = 'This bonus is not on your plan yet. Message support if that looks wrong.';
const UNLOCK_FAILED = 'Could not unlock this bonus right now. Please try again.';

/**
 * Remembered per student on this device only — a convenience so a claimed bonus keeps its
 * "Unlocked" badge. The partner holds the real record, and unlocking again is harmless: it mints a
 * fresh sign-in link or re-opens the bonus link.
 */
const readStored = (key: string | null): Statuses => {
  if (!key) return {};
  try {
    return JSON.parse(localStorage.getItem(key) || '{}') as Statuses;
  } catch {
    return {};
  }
};

const writeStored = (key: string | null, statuses: Statuses) => {
  if (!key) return;
  try {
    const settled = Object.fromEntries(Object.entries(statuses).filter(([, status]) => status !== 'unlocking'));
    localStorage.setItem(key, JSON.stringify(settled));
  } catch {
    // Storage unavailable (private mode): the badge simply resets next visit.
  }
};

export const useBonusUnlocks = () => {
  const user = useAppSelector(selectors.user.selectUser);

  const storageKey = user?.id ? `${STORAGE_PREFIX}:${user.id}` : null;
  const [statuses, setStatuses] = useState<Statuses>(() => readStored(storageKey));
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    setStatuses(readStored(storageKey));
  }, [storageKey]);

  const setStatus = useCallback(
    (id: BonusId, status?: BonusUnlockStatus) => {
      setStatuses((previous) => {
        const next = { ...previous, [id]: status };
        if (!status) delete next[id];
        writeStored(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const setError = (id: BonusId, error?: string) => setErrors((previous) => ({ ...previous, [id]: error }));

  const openHandoff = async (bonus: Bonus, endpoint: string, analyticsEvent: string) => {
    trackAmplitudeEvent(analyticsEvent);
    const previous = statuses[bonus.id];
    setStatus(bonus.id, 'unlocking');

    // Open the tab inside the click, then point it at the link: a window.open after the request
    // resolves is no longer a user gesture, and Safari blocks it.
    const popup = window.open('', '_blank');

    try {
      // A fresh, short-lived link on every click — never cache it.
      const { status, data } = await httpClient.get<{ url: string }>(endpoint);

      if (status >= 200 && status < 300 && data?.url) {
        if (popup) {
          popup.opener = null;
          popup.location.href = data.url;
        } else {
          window.open(data.url, '_blank', 'noopener,noreferrer');
        }
        setStatus(bonus.id, 'unlocked');
        return;
      }

      popup?.close();
      setStatus(bonus.id, previous);
      setError(bonus.id, status === 403 ? NOT_ELIGIBLE : UNLOCK_FAILED);
    } catch (error) {
      console.error(`Failed to unlock ${bonus.name}`, error);
      popup?.close();
      setStatus(bonus.id, previous);
      setError(bonus.id, UNLOCK_FAILED);
    }
  };

  const unlock = async (bonus: Bonus) => {
    if (statuses[bonus.id] === 'unlocking') return;
    setError(bonus.id, undefined);

    switch (bonus.unlock.kind) {
      case 'handoff':
        return openHandoff(bonus, bonus.unlock.endpoint, bonus.unlock.analyticsEvent);
      case 'link': {
        const { url, code } = bonus.unlock;
        const isFirstUnlock = statuses[bonus.id] !== 'unlocked';
        trackAmplitudeEvent(isFirstUnlock ? `${bonus.name} Unlocked` : `${bonus.name} Opened`);

        // A code is needed at that link's checkout, so the first click only reveals it. Opening
        // happens synchronously inside the click, so popup blockers let it through.
        if (!code || !isFirstUnlock) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        return setStatus(bonus.id, 'unlocked');
      }
    }
  };

  return { statuses, errors, unlock };
};
